import { FocusEventHandler, useEffect } from 'react';
import Select, {
  ActionMeta,
  GroupBase,
  MultiValue,
  OptionsOrGroups,
} from 'react-select';
import AsyncSelect from 'react-select/async';

export const customStyles = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 100,
    borderRadius: '10px',
    paddingTop: '0',
  }),
  clearIndicator: (base) => ({
    ...base,
    cursor: 'pointer',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    cursor: 'pointer',
  }),
  input: (base) => ({
    ...base,
    ':focus-visible': {
      outline: 'none',
      outlineOffset: '0',
      boxShadow: 'none !important',
    },
  }),
  control: (base) => ({
    ...base,
    borderRadius: '10px',
    cursor: 'pointer',
  }),
  multiValue: (base) => ({
    ...base,
    borderRadius: '10px',
    whitespace: 'nowrap',
  }),
  multiValueLabel: (base) => ({
    ...base,
    paddingLeft: '10px',
    paddingRight: '4px',
    paddingTop: '4px',
    paddingBottom: '4px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    borderTopRightRadius: '10px',
    borderBottomRightRadius: '10px',
    ':hover': {
      backgroundColor: '#3cb9a2',
      color: 'white',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    borderRadius: '10px',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '10px',
    paddingTop: '0',
    paddingBottom: '0',
  }),
  menuList: (base) => ({
    ...base,
    paddingTop: '0',
    paddingBottom: '0',
  }),
  option: (base, state) => ({
    ...base,
    cursor: 'pointer',
    backgroundColor: state.isFocused ? '#3cb9a2' : base.backgroundColor,
    color: state.isFocused ? 'white' : base.color,
    marginTop: '0',
    ':hover': {
      backgroundColor: '#3cb9a2',
      color: 'white',
    },
    ':first-of-type': {
      borderTopRightRadius: '10px',
      borderTopLeftRadius: '10px',
    },
    ':last-child': {
      borderBottomRightRadius: '10px',
      borderBottomLeftRadius: '10px',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    paddingLeft: '3px',
  }),
};

type MultiSelectInputProps = {
  options?: any[];
  defaultOptions?: any[];
  value?: any;
  onChange: (newValue: MultiValue<any>, actionMeta: ActionMeta<any>) => void;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  async?: boolean;
  loadOptions?: (
    inputValue: string
  ) => Promise<OptionsOrGroups<unknown, GroupBase<unknown>>>;
  noOptionsMessage?: string;
};

export default function MultiSelectInput({
  options,
  defaultOptions,
  value,
  onChange,
  onBlur,
  async,
  loadOptions,
  noOptionsMessage,
}: MultiSelectInputProps) {
  useEffect(() => {
    if (options?.length === 1 && !async) {
      onChange([options[0]], {
        action: 'select-option',
        option: options[0],
      });
    }
  }, []);

  if (async) {
    return (
      <AsyncSelect
        isMulti
        placeholder={"Type to search cities in the Dataset"}
        onChange={onChange}
        onBlur={onBlur}
        cacheOptions
        value={value}
        defaultOptions={defaultOptions}
        menuPortalTarget={document.body}
        loadOptions={loadOptions}
        styles={customStyles}
        classNames={{
          input: () => 'remove-react-select-input-border',
        }}
        noOptionsMessage={() => noOptionsMessage || 'No options'}
      />
    );
  }

  return (
    <Select
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      menuPortalTarget={document.body}
      options={options}
      styles={customStyles}
      classNames={{
        input: () => 'remove-react-select-input-border',
      }}
      isMulti
      noOptionsMessage={() => noOptionsMessage || 'No options'}
    />
  );
}
